import {
  Component,
  computed,
  effect,
  inject,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { PageWrapperComponent } from '../../../../shared/pages/page-wrapper/page-wrapper.component';
import { DatePicker } from 'primeng/datepicker';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
  TimeEntriesService,
  TimesheetEntryDto,
  WorkItemDto,
  WorkItemsService,
  WorkspaceUserDto,
  WorkspacesService,
} from '../../../../api';
import { AuthStore } from '../../../../auth/services/auth.store';
import { debounceTime, finalize, Subject, takeUntil } from 'rxjs';
import { TableModule } from 'primeng/table';
import { TimesheetTableComponent } from '../../components/timesheet-table/timesheet-table.component';
import { Card } from 'primeng/card';
import { Button } from 'primeng/button';
import { MessageService } from '../../../../shared/services/message.service';
import { TimesheetRow } from '../../model/timesheet-row';
import { ModalService } from '../../../../shared/services/modal.service';
import { ConfirmDeleteWorkItemWeekRowComponent } from '../../components/confirm-delete-work-item-week-row/confirm-delete-work-item-week-row.component';
import { NgIf } from '@angular/common';
import { DateTimeUtils } from '../../../../shared/utils/date-time-utils';
import { ErrorHandlerService } from '../../../../shared/services/error-handler.service';
import { Select } from 'primeng/select';
import { TimesheetDayModalComponent } from '../../components/timesheet-day-modal/timesheet-day-modal.component';
import { TimesheetCellClickEvent } from '../../model/timesheet-cell-click-event';

@Component({
  selector: 'app-timesheet-page',
  standalone: true,
  imports: [
    DatePicker,
    PageWrapperComponent,
    FormsModule,
    TableModule,
    TimesheetTableComponent,
    Card,
    Button,
    Select,
    NgIf,
  ],
  templateUrl: './timesheet-page.component.html',
  styleUrl: './timesheet-page.component.css',
})
export class TimesheetPageComponent implements OnDestroy, OnInit {
  @ViewChild('datePicker') datePicker!: DatePicker;

  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly authStore = inject(AuthStore);
  private readonly timeEntriesService = inject(TimeEntriesService);
  private readonly workItemsService = inject(WorkItemsService);
  private readonly workspacesService = inject(WorkspacesService);
  private readonly messageService = inject(MessageService);
  private readonly modalService = inject(ModalService);
  private readonly errorHandler = inject(ErrorHandlerService);
  private readonly destroy$ = new Subject<void>();
  private readonly shiftWeekSubject = new Subject<number>();

  readonly ownerMode = computed(() => this.authStore.isOwner());

  readonly state = signal<{ loading: boolean; error: any | null }>({
    loading: true,
    error: null,
  });

  loadingData = signal<boolean>(false);

  range = signal<Date[] | null>(null);
  start = signal<Date | null>(DateTimeUtils.getWeekRange().start);
  end = signal<Date | null>(DateTimeUtils.getWeekRange().end);
  workItems = signal<WorkItemDto[] | null>(null);
  workspaceUsers = signal<WorkspaceUserDto[]>([]);
  selectedUserId = signal<string | null>(null);

  timeEntries = signal<TimesheetEntryDto[]>([]);

  readonly activeWorkspaceId = computed(() => this.authStore.defaultWorkspace()?.id!);

  readonly currentUserId = computed(() => this.authStore.user()?.id);
  readonly selectedUser = computed(
    () => this.workspaceUsers().find((user) => user.id === this.selectedUserId()) ?? null,
  );
  readonly pageTitle = computed(() =>
    this.ownerMode() ? 'Timesheet - widok właściciela' : 'Timesheet',
  );
  readonly ownerUserOptions = computed(() =>
    this.workspaceUsers().map((user) => ({
      ...user,
      fullName: `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim(),
    })),
  );

  constructor() {
    effect(() => {
      const workspaceId = this.activeWorkspaceId();
      const ownerMode = this.ownerMode();
      const currentUserId = this.currentUserId();

      if (workspaceId && ownerMode) {
        this.fetchWorkspaceUsers();
      }

      if (!this.selectedUserId() && currentUserId) {
        this.selectedUserId.set(currentUserId);
      }
    });

    effect(() => {
      const workspaceId = this.activeWorkspaceId();
      const userId = this.selectedUserId();
      const start = this.start();
      const end = this.end();

      if (workspaceId && userId && start && end) {
        this.fetchWorkItems();
        this.fetchTimesheet();
      }
    });

    this.shiftWeekSubject.pipe(debounceTime(300)).subscribe((days) => {
      this.shiftWeek(days);
    });
  }

  ngOnInit(): void {
    this.loadingData.set(true);

    this.route.queryParamMap.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      const startParam = params.get('start');
      const endParam = params.get('end');
      const userIdParam = params.get('userId');

      let weekUTC: { start: Date; end: Date };

      if (startParam && endParam) {
        weekUTC = {
          start: new Date(startParam),
          end: new Date(endParam),
        };
      } else {
        weekUTC = this.getCurrentWeekUTC();
        this.updateUrl(weekUTC);
      }

      this.setWeek(weekUTC);

      if (this.ownerMode()) {
        if (userIdParam) {
          this.selectedUserId.set(userIdParam);
        } else if (this.currentUserId()) {
          const fallbackUserId = this.currentUserId() ?? null;
          this.selectedUserId.set(fallbackUserId);
          this.updateUrl(weekUTC, fallbackUserId);
        }
      } else if (this.currentUserId()) {
        this.selectedUserId.set(this.currentUserId() ?? null);
      }
    });
  }

  closeDatePicker(): void {
    this.datePicker.overlayVisible = false;
    this.datePicker.hideOverlay();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  protected onDateRangeSelect(date: Date | Date[] | null): void {
    if (!date) return;

    const dates = Array.isArray(date) ? date : [date];
    if (dates.length === 0 || !dates[0]) return;

    const localDate = dates[0];
    const utcDate = DateTimeUtils.localToUTC(localDate);

    const weekUTC = DateTimeUtils.getWeekUTCFromDate(utcDate);
    this.setWeek(weekUTC);
    this.updateUrl(weekUTC);
    this.fetchTimesheet();
    this.closeDatePicker();
  }

  private fetchWorkItems() {
    const activeWorkspaceId = this.activeWorkspaceId();
    const currentUserId = this.selectedUserId();

    if (!activeWorkspaceId || !currentUserId) {
      return;
    }

    if (this.ownerMode() && this.authStore.isOwner()) {
      this.workItemsService.getWorkItems(activeWorkspaceId, 0, 1000, ['name,asc']).subscribe({
        next: (page) => {
          this.workItems.set(page.content ?? []);
        },
      });
      return;
    }

    this.workItemsService.getWorkItemsFilter(activeWorkspaceId).subscribe({
      next: (workItems: WorkItemDto[]) => {
        this.workItems.set(workItems);
      },
    });
  }

  private fetchTimesheet(): void {
    const activeWorkspaceId = this.activeWorkspaceId();
    const currentUserId = this.selectedUserId();
    const start = this.start();
    const end = this.end();

    if (!activeWorkspaceId || !currentUserId || !start || !end) {
      return;
    }

    this.handleInvalidUrlDateRange(start, end);
    this.loadingData.set(true);
    this.timeEntriesService
      .getTimesheet(
        activeWorkspaceId,
        currentUserId,
        start.toISOString(),
        end.toISOString(),
        0,
        0,
        {
          start: start.toISOString(),
          end: end.toISOString(),
          page: 0,
          pageSize: 0,
        },
      )
      .pipe(
        finalize(() => {
          this.loadingData.set(false);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (response) => {
          this.timeEntries.set(response);
        },
      });
  }

  private setWeek(week: { start: Date; end: Date }): void {
    this.start.set(week.start);
    this.end.set(week.end);
    this.range.set([DateTimeUtils.utcToLocal(week.start), DateTimeUtils.utcToLocal(week.end)]);
  }

  private updateUrl(week: { start: Date; end: Date }, userId: string | null = null): void {
    const queryParams: { start: string; end: string; userId?: string } = {
      start: week.start.toISOString(),
      end: week.end.toISOString(),
    };

    if (this.ownerMode() && userId) {
      queryParams.userId = userId;
    }

    this.router.navigate([], {
      queryParams,
      replaceUrl: true,
    });
  }

  private getCurrentWeekUTC(): { start: Date; end: Date } {
    const now = new Date();
    const utcNow = DateTimeUtils.localToUTC(now);
    return DateTimeUtils.getWeekUTCFromDate(utcNow);
  }

  private shiftWeek(days: number): void {
    const start = this.start();
    if (!start) return;

    const target = new Date(start);
    target.setUTCDate(target.getUTCDate() + days);

    const week = DateTimeUtils.getWeekUTCFromDate(target);
    this.setWeek(week);
    this.updateUrl(week);
    this.fetchTimesheet();
  }

  goToPreviousWeek() {
    this.shiftWeekSubject.next(-7);
  }

  goToNextWeek() {
    this.shiftWeekSubject.next(+7);
  }

  onRowDelete(row: TimesheetRow): void {
    this.modalService.open(ConfirmDeleteWorkItemWeekRowComponent, {
      row,
      onConfirm: (selectedRow: TimesheetRow) => this.confirmRowDelete(selectedRow),
    });
  }

  private confirmRowDelete(row: TimesheetRow): void {
    const ids = this.collectRowEntryIds(row);
    if (ids.length === 0) {
      return;
    }

    this.timeEntriesService
      .deleteTimeEntry(this.activeWorkspaceId()!, this.selectedUserId()!, ids)
      .subscribe({
        next: () => {
          this.timeEntries.update((entries) => entries.filter((e) => !ids.includes(e.id!)));
          this.messageService.showInfo('Usunięto pozycje dla ' + row.workItemName);
        },
        error: (err) => {
          this.messageService.showError('Wystąpił błąd: ' + err);
        },
      });
  }

  private collectRowEntryIds(row: TimesheetRow): string[] {
    return row.timeEntries.flatMap((cell) =>
      (cell.entries ?? (cell.id ? [cell] : [])).map((entry) => entry.id!).filter(Boolean),
    );
  }

  protected hasData() {
    return this.workItems() ?? null;
  }

  protected goToAddClient() {
    this.router.navigate(['/clients']);
  }

  private handleInvalidUrlDateRange(start: Date, end: Date) {
    if (
      !DateTimeUtils.parseDate(start) ||
      !DateTimeUtils.parseDate(end) ||
      !DateTimeUtils.isValidRange(start, end) ||
      !DateTimeUtils.clampRange(start, end, 7)
    ) {
      start = DateTimeUtils.getWeekRange().start;
      end = DateTimeUtils.getWeekRange().end;
      this.updateUrl({ start, end });
    }
  }

  protected onCellClick(event: TimesheetCellClickEvent): void {
    const activeWorkspaceId = this.activeWorkspaceId();
    const selectedUserId = this.selectedUserId();

    if (!activeWorkspaceId || !selectedUserId || !event.cell.date || !event.row.workItemId) {
      return;
    }

    const selectedEntryId =
      event.cell.id ?? event.cell.entries?.find((entry) => entry.id)?.id ?? null;

    this.modalService.open(TimesheetDayModalComponent, {
      workspaceId: activeWorkspaceId,
      userId: selectedUserId,
      dateIso: event.cell.date,
      workItemId: event.row.workItemId,
      workItemName: event.row.workItemName,
      clientName: event.row.clientName,
      workItems: this.workItems() ?? [],
      selectedEntryId,
      onChanged: () => this.fetchTimesheet(),
    });
  }

  protected onOwnerUserChange(userId: string | null): void {
    if (!userId) {
      return;
    }

    this.selectedUserId.set(userId);

    const start = this.start();
    const end = this.end();
    if (start && end) {
      this.updateUrl({ start, end }, userId);
    }
  }

  private fetchWorkspaceUsers(): void {
    const activeWorkspaceId = this.activeWorkspaceId();
    if (!activeWorkspaceId) {
      return;
    }

    this.workspacesService
      .getWorkspaceUsers(activeWorkspaceId, 0, 200, ['lastName,asc', 'firstName,asc'])
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.workspaceUsers.set(response.content ?? []);

          if (this.ownerMode() && !this.selectedUserId()) {
            const fallbackUserId = this.currentUserId();
            if (fallbackUserId) {
              this.selectedUserId.set(fallbackUserId);
            }
          }
        },
      });
  }
}
